import Microgame from '../engine/Microgame.js';

export default class GolfGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Golf !`, en: `Golf!`, es: `¡Golf!` },
            controls: { fr: `Maintiens enfoncé pour régler la puissance, relâche pour putter`, en: `Hold to set power, release to putt`, es: `Mantén para ajustar la fuerza, suelta para golpear` },
            objective: { fr: `Mets la balle dans le trou`, en: `Putt the ball into the hole`, es: `Mete la bola en el hoyo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M22 10v6L2 22l6-16 14 4Z"/><circle cx="12" cy="18" r="2"/></svg>`,
            actions: [
                { type: 'wait', duration: 950 },
                { type: 'tap', x: 50, y: 84, duration: 150 },
                { type: 'wait', duration: 420 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.ball = null;
        this.arrow = null;
        this.hole = null;
        this.angle = 0;
        this.swingSpeed = 150;
        this.state = 'aiming'; // aiming, moving, win/lose
        this.velocity = { x: 0, y: 0 };

        // Give plenty of time to aim
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeed = 1 + (speedMult - 1) * (1.5 / 3);
        this.duration = 4500 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "PUTT!";

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);

        this.startRound();
    }

    startRound() {
        this.state = 'aiming';
        this.angle = 0;
        this.swingSpeed = 50; // VERY slow base swing speed for extremely easy aiming

        if (this.hole) this.hole.remove();
        if (this.ball) this.ball.remove();
        if (this.arrow) this.arrow.remove();

        // Constrain ball to the middle area so the hole is roughly in front
        const ballX = this.isPlayback ? 50 : 30 + Math.random() * 40; // 30-70%
        const ballY = this.isPlayback ? 58 : 50 + Math.random() * 15; // 50-65%

        // Place the hole somewhat above the ball to avoid extreme angles
        const offset = this.isPlayback ? 0 : (Math.random() - 0.5) * 20; // -10% to +10% variance
        const holeX = Math.max(10, Math.min(90, ballX + offset));
        const holeY = 15; // Always at the top

        // Hole (Secondary)
        this.hole = document.createElement('div');
        this.hole.style.position = 'absolute';
        this.hole.style.width = '40px';
        this.hole.style.height = '40px';
        this.hole.style.background = '#000';
        this.hole.style.border = `2px solid ${this.secondaryColor}`;
        this.hole.style.borderRadius = '50%';
        this.hole.style.top = `${holeY}%`;
        this.hole.style.left = `${holeX}%`;
        this.hole.style.transform = 'translate(-50%, -50%)';
        this.hole.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
        this.container.appendChild(this.hole);

        // Flag
        const pole = document.createElement('div');
        pole.style.position = 'absolute';
        pole.style.width = '2px';
        pole.style.height = '60px';
        pole.style.background = this.secondaryColor;
        pole.style.bottom = '50%';
        pole.style.left = '50%';
        this.hole.appendChild(pole);

        const flag = document.createElement('div');
        flag.style.position = 'absolute';
        flag.style.width = '20px';
        flag.style.height = '15px';
        flag.style.background = this.secondaryColor;
        flag.style.top = '-60px';
        flag.style.left = '50%';
        this.hole.appendChild(flag);

        // Ball (Primary)
        this.ballXPercent = ballX;
        this.ballYPercent = ballY;

        this.ball = document.createElement('div');
        this.ball.style.position = 'absolute';
        this.ball.style.width = '20px';
        this.ball.style.height = '20px';
        this.ball.style.background = this.primaryColor;
        this.ball.style.borderRadius = '50%';
        this.ball.style.top = `${ballY}%`;
        this.ball.style.left = `${ballX}%`;
        this.ball.style.transform = 'translate(-50%, -50%)';
        this.ball.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.container.appendChild(this.ball);

        this.ballX = null;
        this.ballY = null;

        // Arrow Container
        this.arrow = document.createElement('div');
        this.arrow.style.position = 'absolute';
        this.arrow.style.width = '4px';
        this.arrow.style.height = '250px'; // Long visual aiming line
        this.arrow.style.background = `linear-gradient(to top, ${this.primaryColor}, transparent)`;
        this.arrow.style.top = `${ballY}%`;
        this.arrow.style.left = `${ballX}%`;
        this.arrow.style.transformOrigin = 'top center';
        this.arrow.style.marginTop = '0';
        this.arrow.style.marginLeft = '-2px';
        this.container.appendChild(this.arrow);
    }

    update(dt) {
        if (!this.active) return;
        if (this.waitingForNextRound) return;

        if (this.state === 'aiming') {
            const speedMult = this.engine.speedMultiplier || 1.0;
            // Map speedMult 1.0 -> 4.0 to effectiveMult 1.0 -> 2.5
            const effectiveMult = 1.0 + (speedMult - 1) * (1.5 / 3);
            this.angle += (this.swingSpeed * effectiveMult) * (dt / 1000);

            // SIGNIFICANTLY REDUCED ANGLE SPREAD: -25 to 25 instead of -35 to 35
            // Because the hole is always roughly in front of the ball, you don't need wild angles!
            if (this.angle > 25 || this.angle < -25) {
                this.swingSpeed *= -1;
            }

            // Aiming lines go "up", rotate 180 + angle
            if (this.arrow) this.arrow.style.transform = `rotate(${180 + this.angle}deg)`;
        } else if (this.state === 'moving') {
            const speed = 800;
            const vx = Math.sin(this.angle * Math.PI / 180) * speed;
            const vy = -Math.cos(this.angle * Math.PI / 180) * speed;

            if (!this.ballX) {
                this.ballX = this.container.clientWidth * (this.ballXPercent / 100);
                this.ballY = this.container.clientHeight * (this.ballYPercent / 100);
            }

            this.ballX += vx * (dt / 1000);
            this.ballY += vy * (dt / 1000);

            this.ball.style.left = `${this.ballX}px`;
            this.ball.style.top = `${this.ballY}px`;

            // Check collision with hole
            const holeDOMX = parseFloat(this.hole.style.left) / 100 * this.container.clientWidth;
            const holeDOMY = parseFloat(this.hole.style.top) / 100 * this.container.clientHeight;

            const dist = Math.sqrt((holeDOMX - this.ballX) ** 2 + (holeDOMY - this.ballY) ** 2);

            if (dist < 30) { // Slight bump to hole tolerance from 25 to 30
                if (this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack))) {
                    this.engine.score++;
                    this.engine.updateHUD();

                    this.ball.style.display = 'none';
                    this.hole.style.background = this.primaryColor;

                    this.waitingForNextRound = true;
                    setTimeout(() => {
                        this.waitingForNextRound = false;
                        this.startRound(); // Spawn ball again
                    }, 500);
                } else {
                    this.win();
                    this.ball.style.display = 'none';
                    this.hole.style.background = this.primaryColor;
                    this.hole.style.boxShadow = `0 0 30px ${this.primaryColor}`;
                }
                return;
            }

            // Out of bounds - LOSE! (Reverting to strict one-shot mechanic)
            const containerW = this.container.clientWidth;
            const containerH = this.container.clientHeight;

            if (
                this.ballX < -20 || this.ballX > containerW + 20 ||
                this.ballY < -20 || this.ballY > containerH + 20
            ) {
                if (this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack))) {
                    // Try again, but we don't automatically respawn.
                    this.startRound();
                } else {
                    this.lose();
                }
            }
        }
    }

    onDown() {
        if (!this.active || this.state !== 'aiming') return;
        this.state = 'moving';
        this.arrow.style.display = 'none';
        this.hideTimer = true; // Stop timer when ball is shot
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
